import React, { useState, useEffect, useRef } from 'react';
import { getColorById } from '../data/mockData';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Label } from './ui/label';
import { useTranslation } from '../context/TranslationContext';

const KeralitDrawingBoard = ({ 
  width = 1500, 
  height = 1500,
  exteriorColor = 'anthracite',
  interiorColor = 'white',
  lineType = 'horizontal', // 'horizontal' or 'vertical'
  lineSpacing = '19cm', // '19cm' or '14cm'
  onWidthChange,
  onHeightChange,
  onLineTypeChange,
  onLineSpacingChange
}) => {
  const { t } = useTranslation();
  const [widthInput, setWidthInput] = useState(String(width));
  const [heightInput, setHeightInput] = useState(String(height));
  const [isHovered, setIsHovered] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const svgRef = useRef(null);

  // Sync inputs with props
  useEffect(() => {
    setWidthInput(String(width));
  }, [width]);

  useEffect(() => {
    setHeightInput(String(height));
  }, [height]);

  // Get colors
  const exteriorColorData = getColorById(exteriorColor);
  const interiorColorData = getColorById(interiorColor);
  const exteriorColorHex = exteriorColorData?.hex || '#2C2C2C';
  const interiorColorHex = interiorColorData?.hex || '#FFFFFF';

  // SVG dimensions (matching DoorDrawingBoard and Kozijnen)
  const baseSvgWidth = 900;
  const baseSvgHeight = 800;
  const scale = Math.min(baseSvgWidth / width, baseSvgHeight / height) * 0.8;
  const scaledWidth = width * scale;
  const scaledHeight = height * scale;

  // Frame thickness
  const frameThickness = 54 * scale;

  // Ruler space (matching DoorDrawingBoard style)
  const rulerSpaceRight = 100;
  const rulerSpaceBottom = 100;

  // Calculate SVG viewBox and dimensions
  const svgWidth = baseSvgWidth + rulerSpaceRight;
  const svgHeight = baseSvgHeight + rulerSpaceBottom;

  // Center the frame (start position)
  const startX = (baseSvgWidth - scaledWidth) / 2;
  const startY = (baseSvgHeight - scaledHeight) / 2;
  
  const frameX = startX;
  const frameY = startY;

  // Inner frame (interior color)
  const innerFrameX = frameX + frameThickness;
  const innerFrameY = frameY + frameThickness;
  const innerFrameWidth = scaledWidth - (frameThickness * 2);
  const innerFrameHeight = scaledHeight - (frameThickness * 2);


  // Constants for dimension validation
  const MIN_DIMENSION = 500;
  const MAX_DIMENSION = 8000;

  // Render panel lines (matching Kozijnen style)
  const renderPanelLines = (lineType, lineSpacing, x, y, width, height) => {
    // Ensure we have a valid lineType, default to 'horizontal'
    const currentLineType = lineType || 'horizontal';
    if (currentLineType !== 'horizontal' && currentLineType !== 'vertical') {
      return null;
    }

    const lineColor = '#D0D4DA'; // Same color as style-5 lines
    const lineWidth = 0.5; // Same stroke width as style-5
    
    // For 19cm: use 8 lines (current design)
    // For 14cm: calculate more lines to fill the panel with reduced spacing
    const baseNumberOfLines = 8;
    const spacingFactor = lineSpacing === '14cm' ? 14 / 19 : 1;
    
    // Calculate number of lines based on spacing
    // When spacing is reduced (14cm), we need more lines to fill the same space
    const numberOfLines = lineSpacing === '14cm' 
      ? Math.ceil(baseNumberOfLines / spacingFactor) 
      : baseNumberOfLines;
    
    if (currentLineType === 'horizontal') {
      // Calculate spacing: divide height by (numberOfLines + 1) for even distribution
      const lineSpacing = height / (numberOfLines + 1);
      // Draw horizontal lines evenly distributed across the panel
      return (
        <g pointerEvents="none">
          {Array.from({ length: numberOfLines }, (_, i) => {
            const lineY = y + (lineSpacing * (i + 1));
            return (
              <line
                key={`h-line-${i}`}
                x1={x}
                y1={lineY}
                x2={x + width}
                y2={lineY}
                stroke={lineColor}
                strokeWidth={lineWidth}
              />
            );
          })}
        </g>
      );
    } else if (currentLineType === 'vertical') {
      // Calculate spacing: divide width by (numberOfLines + 1) for even distribution
      const lineSpacing = width / (numberOfLines + 1);
      // Draw vertical lines evenly distributed across the panel
      return (
        <g pointerEvents="none">
          {Array.from({ length: numberOfLines }, (_, i) => {
            const lineX = x + (lineSpacing * (i + 1));
            return (
              <line
                key={`v-line-${i}`}
                x1={lineX}
                y1={y}
                x2={lineX}
                y2={y + height}
                stroke={lineColor}
                strokeWidth={lineWidth}
              />
            );
          })}
        </g>
      );
    }

    return null;
  };

  return (
    <>
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        className="bg-white max-w-full max-h-full"
        style={{ cursor: isHovered ? 'pointer' : 'default', minHeight: '500px' }}
      >
        {/* Background (flat drawing board) */}
        <rect width={svgWidth} height={svgHeight} fill="#F5F5F5" />
        
        {/* Outer Frame (Exterior Color) */}
          <rect
            x={frameX}
            y={frameY}
            width={scaledWidth}
            height={scaledHeight}
            fill={exteriorColorHex}
            stroke="#E5E7EB"
            strokeWidth="1"
          />

          {/* Inner Frame (Interior Color) - Clickable */}
          <rect
            x={innerFrameX}
            y={innerFrameY}
            width={innerFrameWidth}
            height={innerFrameHeight}
            fill={interiorColorHex}
            stroke="#E5E7EB"
            strokeWidth="0.5"
            style={{ cursor: 'pointer', pointerEvents: 'auto' }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={(e) => {
              e.stopPropagation();
              setDialogOpen(true);
            }}
          >
            <title>{t('drawing.clickToConfigurePanelLines')}</title>
          </rect>

          {/* Hover highlight overlay */}
          {isHovered && (
            <rect
              x={innerFrameX}
              y={innerFrameY}
              width={innerFrameWidth}
              height={innerFrameHeight}
              fill="rgba(255, 165, 0, 0.15)"
              stroke="rgba(255, 140, 0, 0.6)"
              strokeWidth="2"
              style={{ pointerEvents: 'none', cursor: 'pointer' }}
            />
          )}

          {/* Panel Lines */}
          {renderPanelLines(lineType || 'horizontal', lineSpacing || '19cm', innerFrameX, innerFrameY, innerFrameWidth, innerFrameHeight)}

        {/* Dimension Lines - Width (Bottom) */}
        <g>
          <line
            x1={startX}
            y1={startY + scaledHeight + 50}
            x2={startX + scaledWidth}
            y2={startY + scaledHeight + 50}
            stroke="#2C2C2C"
            strokeWidth="1.5"
          />
          <line
            x1={startX}
            y1={startY + scaledHeight + 45}
            x2={startX}
            y2={startY + scaledHeight + 55}
            stroke="#2C2C2C"
            strokeWidth="1.5"
          />
          <line
            x1={startX + scaledWidth}
            y1={startY + scaledHeight + 45}
            x2={startX + scaledWidth}
            y2={startY + scaledHeight + 55}
            stroke="#2C2C2C"
            strokeWidth="1.5"
          />
          <foreignObject x={startX + scaledWidth / 2 - 50} y={startY + scaledHeight + 55} width="100" height="24">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
              <input
                type="number"
                value={widthInput}
                onChange={(e) => setWidthInput(e.target.value)}
                onBlur={() => {
                  if (!onWidthChange) return;
                  const parsed = parseInt(widthInput, 10);
                  if (!Number.isNaN(parsed) && parsed >= MIN_DIMENSION && parsed <= MAX_DIMENSION) {
                    setWidthInput(String(parsed));
                    onWidthChange(parsed);
                  } else {
                    setWidthInput(String(width));
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.target.blur();
                    if (!onWidthChange) return;
                    const parsed = parseInt(widthInput, 10);
                    if (!Number.isNaN(parsed) && parsed >= MIN_DIMENSION && parsed <= MAX_DIMENSION) {
                      setWidthInput(String(parsed));
                      onWidthChange(parsed);
                    } else {
                      setWidthInput(String(width));
                    }
                  }
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'center',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#2C2C2C',
                  outline: 'none',
                  padding: 0,
                  margin: 0,
                  WebkitAppearance: 'none',
                  MozAppearance: 'textfield',
                  width: `${String(widthInput).length * 10 + 10}px`,
                  maxWidth: '80px',
                }}
              />
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#2C2C2C', opacity: 0.7 }}>mm</span>
            </div>
          </foreignObject>
        </g>

        {/* Dimension Lines - Height (Right) */}
        <g>
          <line
            x1={startX + scaledWidth + 50}
            y1={startY}
            x2={startX + scaledWidth + 50}
            y2={startY + scaledHeight}
            stroke="#2C2C2C"
            strokeWidth="1.5"
          />
          <line
            x1={startX + scaledWidth + 45}
            y1={startY}
            x2={startX + scaledWidth + 55}
            y2={startY}
            stroke="#2C2C2C"
            strokeWidth="1.5"
          />
          <line
            x1={startX + scaledWidth + 45}
            y1={startY + scaledHeight}
            x2={startX + scaledWidth + 55}
            y2={startY + scaledHeight}
            stroke="#2C2C2C"
            strokeWidth="1.5"
          />
          <foreignObject x={startX + scaledWidth + 60} y={startY + scaledHeight / 2 - 12} width="100" height="24">
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <input
                type="number"
                value={heightInput}
                onChange={(e) => setHeightInput(e.target.value)}
                onBlur={() => {
                  if (!onHeightChange) return;
                  const parsed = parseInt(heightInput, 10);
                  if (!Number.isNaN(parsed) && parsed >= MIN_DIMENSION && parsed <= MAX_DIMENSION) {
                    setHeightInput(String(parsed));
                    onHeightChange(parsed);
                  } else {
                    setHeightInput(String(height));
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.target.blur();
                    if (!onHeightChange) return;
                    const parsed = parseInt(heightInput, 10);
                    if (!Number.isNaN(parsed) && parsed >= MIN_DIMENSION && parsed <= MAX_DIMENSION) {
                      setHeightInput(String(parsed));
                      onHeightChange(parsed);
                    } else {
                      setHeightInput(String(height));
                    }
                  }
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#2C2C2C',
                  outline: 'none',
                  padding: 0,
                  margin: 0,
                  WebkitAppearance: 'none',
                  MozAppearance: 'textfield',
                  width: `${String(heightInput).length * 10 + 8}px`,
                  maxWidth: '80px',
                }}
              />
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#2C2C2C', opacity: 0.7 }}>mm</span>
            </div>
          </foreignObject>
        </g>
      </svg>

      {/* Dialog for Panel Lines Configuration */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('drawing.panelConfiguration')}</DialogTitle>
            <DialogDescription>
              {t('drawing.configurePanelLineType')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>{t('drawing.panelLines')}</Label>
              <Select
                value={lineType || 'horizontal'}
                onValueChange={(value) => {
                  if (onLineTypeChange) {
                    onLineTypeChange(value);
                  }
                }}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t('drawing.selectLineType')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="horizontal">{t('drawing.horizontalLines')}</SelectItem>
                  <SelectItem value="vertical">{t('drawing.verticalLines')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('options.lineSpacing')}</Label>
              <Select
                value={lineSpacing || '19cm'}
                onValueChange={(value) => {
                  if (onLineSpacingChange) {
                    onLineSpacingChange(value);
                  }
                }}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t('drawing.selectSpacing')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="19cm">{t('drawing.lineSpacing19cm')}</SelectItem>
                  <SelectItem value="14cm">{t('drawing.lineSpacing14cm')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default KeralitDrawingBoard;

